// Stats extractors + formatters. Pure functions over (state, viewer); no
// DOM. Wrappers around the colorbar stats primitives so the Inspector
// can share one numerical-formatting source of truth with the colorbar
// stack (Stage 2).

import {
    calculateColormapBoundsFromArray,
    formatStatsMedian1Std,
    formatStatsRange,
} from '../colorbar';
import { isOutsideDomain, type ResolvedDomain } from '@store/selections';
import type { Selection, State } from '@store/schema';
import type { ViewerContext } from '@app/context';

export interface FieldStats {
    /** "0.123 ± 0.045" — empty string if no data. */
    medianStdLabel: string;
    /** "[-1.234, 5.678]" — empty string if no data. */
    rangeLabel: string;
    /** Trailing units suffix (e.g. " nT"); empty when units unknown. */
    unitsSuffix: string;
}

export interface GridExtentsRow {
    axis: 'x' | 'y' | 'z';
    min: number;
    max: number;
    n: number;
}

function currentFieldArray(viewer: ViewerContext): Float32Array | null {
    const f = viewer.field;
    if (f.currentComponent === 'magnitude') return f.gridFlat ?? null;
    const arr = f.data ? f.data[f.currentComponent] : null;
    return arr ?? null;
}

function unitsSuffixFor(viewer: ViewerContext): string {
    const meta = viewer.field.metadata ?? ({} as Record<string, unknown>);
    const u = (meta as { field_units?: string }).field_units || viewer.sceneMeta.units || '';
    return u ? ` ${u}` : '';
}

// Global stats require a full-array median sort (O(N log N) + a Float32Array
// copy — see calculateColormapBounds). They depend only on the active field
// array and centerOnZero, NOT on probe position, yet the Inspector re-renders
// the probe card on every MOVE_SELECTION during a drag. Memoize on array
// identity + centerOnZero so a drag is a cache hit (units suffix is cheap and
// recomputed each call to stay correct across a units change). The array
// reference changes on dataset / component switch, which invalidates naturally.
let globalStatsMemo: { arr: Float32Array; centerOnZero: boolean; medianStdLabel: string; rangeLabel: string } | null = null;

/** Median±σ, range, and a units suffix for the currently-active component. */
export function computeGlobalFieldStats(
    state: State,
    viewer: ViewerContext,
): FieldStats {
    const arr = currentFieldArray(viewer);
    const unitsSuffix = unitsSuffixFor(viewer);
    if (!arr || !arr.length) {
        return { medianStdLabel: '', rangeLabel: '', unitsSuffix };
    }
    const centerOnZero = !!state.params.centerOnZero;
    if (globalStatsMemo && globalStatsMemo.arr === arr && globalStatsMemo.centerOnZero === centerOnZero) {
        return { medianStdLabel: globalStatsMemo.medianStdLabel, rangeLabel: globalStatsMemo.rangeLabel, unitsSuffix };
    }
    const stats = calculateColormapBoundsFromArray(arr, { centerOnZero });
    const medianStdLabel = formatStatsMedian1Std(stats);
    const rangeLabel = formatStatsRange(stats);
    globalStatsMemo = { arr, centerOnZero, medianStdLabel, rangeLabel };
    return { medianStdLabel, rangeLabel, unitsSuffix };
}

/**
 * Field stats narrowed to a selection's bounds. Walks the field array,
 * filtering by the resolved domain (box AABB or sphere mask), and feeds the
 * surviving values to the same colorbar-stats path. Allocates one filtered
 * Float32Array per call — cheap relative to the masking work itself, and
 * keeps the colorbar-stats helper untouched.
 */
export function computeMaskedFieldStats(
    state: State,
    viewer: ViewerContext,
    selection: Selection,
): FieldStats {
    const arr = currentFieldArray(viewer);
    const unitsSuffix = unitsSuffixFor(viewer);
    if (!arr || !arr.length) {
        return { medianStdLabel: '', rangeLabel: '', unitsSuffix };
    }
    const grid = viewer.grid;
    const domain: ResolvedDomain = selection.kind === 'box'
        ? { kind: 'box', lower: selection.lower, upper: selection.upper }
        : { kind: 'sphere', center: selection.center, radius: selection.radius, keep: selection.keep };

    // Field data layout: idx = i * (ny * nz) + j * nz + k, where i indexes
    // physical X, j physical Y, k physical Z. Same convention used by
    // probe.ts + slices.ts.
    const nx = grid.nx, ny = grid.ny, nz = grid.nz;
    const dx = nx > 1 ? (grid.xMax - grid.xMin) / (nx - 1) : 0;
    const dy = ny > 1 ? (grid.yMax - grid.yMin) / (ny - 1) : 0;
    const dz = nz > 1 ? (grid.zMax - grid.zMin) / (nz - 1) : 0;

    // Pre-allocate at worst-case (whole grid); shrink at the end.
    const buf = new Float32Array(arr.length);
    let count = 0;
    for (let i = 0; i < nx; i++) {
        const x = grid.xMin + i * dx;
        for (let j = 0; j < ny; j++) {
            const y = grid.yMin + j * dy;
            const base = i * (ny * nz) + j * nz;
            for (let k = 0; k < nz; k++) {
                const z = grid.zMin + k * dz;
                if (isOutsideDomain(x, y, z, domain)) continue;
                buf[count++] = arr[base + k] ?? 0;
            }
        }
    }
    if (count === 0) {
        return { medianStdLabel: '—', rangeLabel: '—', unitsSuffix };
    }
    const filtered = buf.subarray(0, count) as Float32Array;
    const stats = calculateColormapBoundsFromArray(filtered, {
        centerOnZero: !!state.params.centerOnZero,
    });
    return {
        medianStdLabel: formatStatsMedian1Std(stats),
        rangeLabel: formatStatsRange(stats),
        unitsSuffix,
    };
}

export function gridExtentRows(viewer: ViewerContext): readonly GridExtentsRow[] {
    const g = viewer.grid;
    return [
        { axis: 'x', min: g.xMin, max: g.xMax, n: g.nx },
        { axis: 'y', min: g.yMin, max: g.yMax, n: g.ny },
        { axis: 'z', min: g.zMin, max: g.zMax, n: g.nz },
    ];
}

export function coordSystemLabel(viewer: ViewerContext): string {
    const meta = viewer.field.metadata ?? ({} as Record<string, unknown>);
    return (meta as { coordinate_system?: string }).coordinate_system
        || viewer.sceneMeta.coordinateSystem
        || 'GSM';
}

/** Snapshot/time label from field metadata when present; empty otherwise. */
export function timestepLabel(viewer: ViewerContext): string {
    const t = viewer.field.dataTime;
    if (t === null || t === undefined || t === '') return '';
    const u = viewer.field.dataTimeUnits ? ` ${viewer.field.dataTimeUnits}` : '';
    return `${String(t)}${u}`;
}

/** Probe value at the focused cube selection, with units; '—' when absent. */
export function probeValueLabel(state: State, viewer: ViewerContext): string {
    const sel = state.selection;
    if (!sel.sample) return '—';
    const suffix = unitsSuffixFor(viewer);
    return `${sel.sample.value.toFixed(4)}${suffix}`;
}
